import React, { useCallback, useMemo, useRef, useState, useContext } from 'react'
import ModalContext, { ModalCallback } from './ModalContext'
import noop from 'lib/function/noop'

export interface ModalElementContextValue<R> {
  open?: boolean;
  resolve: NonNullable<R> extends never ? (result?: undefined) => void : (result: R) => void;
  afterClose: () => void;
}

const defaultValue: ModalElementContextValue<undefined> = {
  open: false,
  resolve: noop,
  afterClose: noop,
}

export const ModalElementContext = React.createContext<ModalElementContextValue<undefined>>(defaultValue)

export function useIsInModal() {
  return useContext(ModalElementContext) !== defaultValue
}

function isModalCallback(modal: React.ReactElement | ModalCallback): modal is ModalCallback {
  return typeof modal === 'function' && !React.isValidElement(modal)
}

function ModalProvider(props: React.PropsWithChildren) {
  const [modal, setModal] = useState<{
    resolve: (val?: any) => any,
    element: React.ReactElement
  }>()
  // This holds the promise for the current modal that is open
  const modalPromise = useRef<Promise<void> | undefined>(undefined)
  const afterCloseRef = useRef<(() => any) | undefined>(undefined)
  const [open, setOpen] = useState<boolean>()
  const showModal = useCallback((nextModal: React.ReactElement | ModalCallback) => {
    const modalResolve = (resolve: (value?: any) => void) => {
      const element = isModalCallback(nextModal) ? nextModal(resolve) : nextModal
      setModal({
        element,
        resolve,
      })
      setOpen(true)
    }

    const afterResolve = (val: any) => {
      // start closing the modal, we still want the element rendered so it doesn't jank around
      setOpen(false)
      // we need to wait till it's closed before we can fully resolve
      // as that's when the modal is truly "done"
      const onAfterClose = new Promise<any>(resolve => {
        afterCloseRef.current = () => resolve(val)
      })
      // chain the promise until after the close is done
      return onAfterClose
    }

    if (modalPromise.current) {
      // an existing modal is already open and we're trying to show a new one...
      // wait for the old one to close then show the next one
      return modalPromise.current.then(() => {
        const promise = new Promise(modalResolve).then(afterResolve)
        modalPromise.current = promise
        return promise
      })
    }
    const promise = new Promise(modalResolve).then(afterResolve)
    modalPromise.current = promise
    return promise
  }, [])

  const elementContextValue = useMemo(() => ({
    open,
    resolve: modal?.resolve ?? noop,
    afterClose: () => {
      // remove the modal element from being rendered, we're done with it
      setModal(undefined)
      // after close is done, finish the showModal call and resolve it back to the caller
      afterCloseRef.current?.()
      afterCloseRef.current = undefined
      modalPromise.current = undefined
    },
  }), [modal, open])

  return <>
    <ModalElementContext value={elementContextValue}>
      {modal?.element}
    </ModalElementContext>
    <ModalContext value={showModal}>
      {props.children}
    </ModalContext>
  </>
}

export default ModalProvider
